import { Node as ProseMirrorNode } from 'prosemirror-model';
import { MarkType } from '@tiptap/pm/model';
import { getMarkRange } from './getMarkRange';
import { MarkRange } from '@tiptap/core';
import { isEmpty } from 'lodash';
import { getMarksBetween } from '@tiptap/react';

/**
 * Retrieves all the ranges of the given types, and extends them to include the full mark range.
 */
export const getExtendedMarksBetween = (
    from: number,
    to: number,
    doc: ProseMirrorNode,
    markTypes: MarkType[] = [],
): MarkRange[] => {
    const markRanges = getMarksBetween(from, to, doc);
    const validMarkRanges = isEmpty(markTypes)
        ? markRanges
        : markRanges.filter(({ mark }) => markTypes.some((markType) => mark.type === markType));

    // Now that we know the marks in between the two positions, we need to grow the ranges of the marks to
    // include the full mark range.
    // The above function will also split mark ranges whose marks change in the middle of the range (e.g. if there's
    //  a bold mark in the middle), so we want to join those ranges back together.
    const extendedMarkRanges = validMarkRanges.map((markRange) => {
        const { from, mark } = markRange;

        const $pos = doc.resolve(from);
        const extendedRange = getMarkRange($pos, mark.type);

        if (!extendedRange) return markRange;

        return {
            ...extendedRange,
            mark,
        };
    });

    // Filter out duplicates
    const uniqueRanges = new Map();
    extendedMarkRanges.forEach((range) => {
        const key = `${range.from}-${range.to}-${range.mark.type.name}`;

        if (uniqueRanges.has(key)) return;

        uniqueRanges.set(key, range);
    });

    // Convert back to array
    return Array.from(uniqueRanges.values());
};
